/**
 * Deliverables Panel — slide-out panel showing all completed deliverables.
 * Accessible from HUD "Work" tab.
 * Glass-morphism style matching existing UI.
 *
 * Also handles the deliverable viewer (full content + copy/export).
 */

import { dataManager } from '@/data/data-manager';
import { extractDeskIndex } from '@/utils/worker-utils';
import type { DeliverableRecord, DeliverableContent } from '@/data/types';
import { formatMarkdown, escapeHtml } from '@/utils/markdown';

export class DeliverablesPanel {
  private panel: HTMLElement;
  private listEl: HTMLElement;
  private isOpen = false;
  private _newCount = 0;
  private _lastViewedAt = Date.now();

  constructor() {
    // Create panel
    this.panel = document.createElement('div');
    this.panel.id = 'deliverables-panel';
    this.panel.innerHTML = `
      <div class="deliverables-header">
        <span class="deliverables-title">Work</span>
        <button class="deliverables-close">&times;</button>
      </div>
      <div class="deliverables-list"></div>
    `;
    document.body.appendChild(this.panel);
    this.listEl = this.panel.querySelector('.deliverables-list')!;

    // Close button
    this.panel.querySelector('.deliverables-close')!
      .addEventListener('click', () => this.close());

    // Listen for view-deliverable events (from task queue clicks)
    window.addEventListener('view-deliverable', ((e: CustomEvent) => {
      this.showViewer(e.detail as DeliverableContent);
    }) as EventListener);
  }

  async open(): Promise<void> {
    this.isOpen = true;
    this.panel.classList.add('open');
    this._lastViewedAt = Date.now();
    this._newCount = 0;

    // Dispatch badge update
    window.dispatchEvent(new CustomEvent('deliverable-badge-update', {
      detail: { count: 0 },
    }));

    await this.refresh();
  }

  close(): void {
    this.isOpen = false;
    this.panel.classList.remove('open');
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  get newCount(): number { return this._newCount; }

  /** Refresh the deliverables list from backend */
  async refresh(): Promise<void> {
    try {
      const deliverables = await dataManager.listDeliverables();

      if (deliverables.length === 0) {
        this.listEl.innerHTML = '<div class="deliverable-list-empty">No deliverables yet. Ask Alex to create a task plan!</div>';
        return;
      }

      this.listEl.innerHTML = '';
      for (const d of deliverables) {
        this.renderItem(d);
      }

      // Count new deliverables since last viewed
      this._newCount = deliverables.filter(d => d.created_at > this._lastViewedAt).length;
      if (this._newCount > 0 && !this.isOpen) {
        window.dispatchEvent(new CustomEvent('deliverable-badge-update', {
          detail: { count: this._newCount },
        }));
      }
    } catch (err) {
      console.warn('Failed to load deliverables:', err);
    }
  }

  private renderItem(d: DeliverableRecord): void {
    const el = document.createElement('div');
    el.className = 'deliverable-list-item';

    const workerName = this.resolveWorkerName(d.worker_id);
    const timeAgo = this.formatTimeAgo(d.created_at);

    el.innerHTML = `
      <div class="deliverable-list-title">${escapeHtml(d.title)}</div>
      <div class="deliverable-list-meta">
        <span class="deliverable-list-worker">${escapeHtml(workerName)}</span>
        <span class="deliverable-list-format">${escapeHtml(d.format)}</span>
        <span class="deliverable-list-date">${timeAgo}</span>
      </div>
      ${d.content_preview ? `<div class="deliverable-list-preview">${escapeHtml(this.cleanDeliverableText(d.content_preview).slice(0, 120))}...</div>` : ''}
    `;

    el.addEventListener('click', () => this.loadAndView(d.id));
    this.listEl.appendChild(el);
  }

  private async loadAndView(id: string): Promise<void> {
    try {
      const content = await dataManager.getDeliverableContent(id);
      this.showViewer(content);
    } catch (err) {
      console.warn('Failed to load deliverable content:', err);
    }
  }

  private showViewer(deliverable: DeliverableContent): void {
    // Remove existing viewer if any
    document.querySelector('.deliverable-viewer')?.remove();

    const rawContent = deliverable.content?.content || 'No content available';
    const contentText = this.cleanDeliverableText(rawContent);

    const viewer = document.createElement('div');
    viewer.className = 'deliverable-viewer';
    viewer.innerHTML = `
      <div class="deliverable-viewer-content">
        <div class="viewer-header">
          <span class="viewer-title">${escapeHtml(deliverable.title)}</span>
          <div class="viewer-actions">
            <button class="viewer-copy-btn">Copy</button>
            <button class="viewer-close-btn">Close</button>
          </div>
        </div>
        <div class="viewer-body">${formatMarkdown(contentText)}</div>
      </div>
    `;
    document.body.appendChild(viewer);

    // Close on backdrop click
    viewer.addEventListener('click', (e) => {
      if (e.target === viewer) viewer.remove();
    });

    // Close button
    viewer.querySelector('.viewer-close-btn')!.addEventListener('click', () => {
      viewer.remove();
    });

    // Copy button
    const copyBtn = viewer.querySelector('.viewer-copy-btn') as HTMLButtonElement;
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(contentText);
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
      } catch (err) {
        console.warn('[Deliverables] Clipboard API failed, using fallback:', err);
        const textarea = document.createElement('textarea');
        textarea.value = contentText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
        copyBtn.textContent = 'Copied!';
        setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
      }
    });
  }

  private resolveWorkerName(workerId: string): string {
    if (dataManager.initialized) {
      const idx = extractDeskIndex(workerId);
      if (idx !== null) {
        return dataManager.getWorkerName(idx);
      }
    }
    return 'Worker';
  }

  private formatTimeAgo(timestamp: number): string {
    const diff = Date.now() - timestamp;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  /** Strip any leftover [DELIVERABLE]/[ACTION] tags from content */
  private cleanDeliverableText(text: string): string {
    let cleaned = text;
    // Extract content from [DELIVERABLE]...[/DELIVERABLE] wrapper
    const match = cleaned.match(/\[DELIVERABLE\]\s*(?:Title:\s*[^\n]*?(?:\n---\n|\s*---\s*))?([\s\S]*?)\[\/DELIVERABLE\]/i);
    if (match) {
      cleaned = match[1].trim();
    } else {
      // Fallback: just remove tags
      cleaned = cleaned.replace(/\[DELIVERABLE\]/gi, '').replace(/\[\/DELIVERABLE\]/gi, '').trim();
      cleaned = cleaned.replace(/^Title:\s*[^\n]*?\n---\n?/i, '').trim();
    }
    cleaned = cleaned.replace(/\[ACTION:\s*\w+\]/gi, '').trim();
    return cleaned;
  }

  dispose(): void {
    document.querySelector('.deliverable-viewer')?.remove();
    this.panel.remove();
  }
}
